<template>
  <div :class="['ls-conversation-message', { 'border-bottom': !isLast }]">
    <div class="ls-conversation-message--upper columns">
      <!-- mobile header -->
      <div v-if="!screenSize.desktop" class="ls-conversation-message--mobile-header">
        <span
          test-id="name"
          :class="[
            'has-text-weight-bold',
            { 'ls-conversation-message--primary': message.user.customer_id },
            { 'ls-conversation-message--gray': message.user.contractor_id },
            { 'ls-conversation-message--tertiary': message.user.is_admin },
          ]"
        >
          {{ renderName }}
        </span>
        <span class="ls-conversation-message--timestamp" test-id="timestamp">
          {{ renderTimeStamp }}
        </span>
      </div>

      <!-- end mobile header -->

      <div
        :class="['ls-conversation-message--message-text', { 'column is-10': screenSize.desktop }]"
      >
        <span
          v-if="screenSize.desktop"
          test-id="name"
          :class="[
            'has-text-weight-bold',
            { 'ls-conversation-message--primary': message.user.customer_id },
            { 'ls-conversation-message--gray': message.user.contractor_id },
            { 'ls-conversation-message--tertiary': message.user.is_admin },
          ]"
        >
          {{ renderName }}:
        </span>
        <span class="ls-conversation-message--message-text" test-id="message_text">
          {{ message.text }}</span
        >
        <LsImage v-if="message.photo_url" :src="message.photo_url" />
      </div>
      <div v-if="screenSize.desktop" class="ls-conversation-message--timestamp column">
        <span test-id="timestamp">{{ renderTimeStamp }}</span>
      </div>
    </div>

    <div v-if="hasIntent">
      <LsButton
        test-id="intent_button"
        track-id="ls_coversation_message-intent_button"
        :text="getIntentButtonInfo"
        bg-color="primary"
        text-color="white"
        :disabled="hasEntity && Boolean(message.link.entity.rejected_at)"
        :loading="conversation.loadingStatus.isUpdatingMessageID === message.message_id"
        @click="onIntentActionPress({ link: message.link })"
      />

      <div v-if="getMessageForRelevantProperty" class="ls-conversation-message--relevant-property">
        <div>!</div>
        <span>This message is in regards to {{ getMessageForRelevantProperty }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueTypes from 'vue-types';
import moment from 'moment';
import _ from 'lodash';
import { mapActions, mapState, mapGetters } from 'vuex';
import {
  CONVERSATION__REJECT_DAY_SWITCH,
  PROPERTY_MANAGER__CURRENT_PROPERTY_ID,
} from '@/store/types';
import { LsButton, LsImage } from '@/components/common';
import { lsPropTypeHelpers } from 'ls-vue-common';
import { themedComponentMixin } from '@/mixins';

export default {
  name: 'ConversationMessage',
  components: { LsButton, LsImage },
  mixins: [themedComponentMixin],
  props: {
    isLast: VueTypes.bool.def(false),
    message: VueTypes.shape({
      created_at: VueTypes.string.isRequired, // YYYY-MM-DD HH:mm:ss
      message_id: VueTypes.number.isRequired,
      text: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
      user: VueTypes.shape({
        customer_id: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        contractor_id: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        // should use absence of customer and contractor id instead of this flag
        // as deactivated accounts lose the is_admin flag
        user_id: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        first_name: VueTypes.string,
        last_name: VueTypes.string,
      }).isRequired.loose, // need loose here since the object has additional unused properties
      link: VueTypes.shape({
        intent: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
        entity: VueTypes.oneOfType([Object, null]),
      }),
      photo_url: VueTypes.string,
    }).isRequired.loose, // need loose here since the object has additional unused properties
  },
  computed: {
    ...mapState(['screenSize', 'conversation']),
    ...mapGetters([PROPERTY_MANAGER__CURRENT_PROPERTY_ID]),
    hasIntent() {
      return _.get(this.message, 'link.intent', false);
    },
    hasEntity() {
      return _.get(this.message, 'link.entity', false);
    },
    getMessageForRelevantProperty() {
      const { currentConversation } = this.conversation;

      if (this.hasEntity) {
        const { scheduleEvent, property_id } = this.hasEntity;

        const propertyId =
          this.hasIntent === 'property.details' ? property_id : scheduleEvent.schedule.property.id;

        if (
          currentConversation.customer.properties.length === 1 ||
          propertyId === this[PROPERTY_MANAGER__CURRENT_PROPERTY_ID]
        ) {
          return null;
        }

        const relevantProperty = _.find(currentConversation.customer.properties, (property) => {
          return property.id === propertyId;
        });

        return relevantProperty ? `${relevantProperty.street}` : null;
      }

      return null;
    },
    renderTimeStamp() {
      const { created_at } = this.message;
      const currentTime = moment.utc(created_at).local();

      moment.updateLocale('en', {
        relativeTime: {
          future: 'in %s',
          past: '%s ago',
          s: 'seconds',
          ss: '%ss',
          m: '%dm',
          mm: '%dm',
          h: 'an hour',
          hh: '%dh',
          d: 'a day',
          dd: '%dd',
          M: '%dmo',
          MM: '%dmo',
          y: '%dy',
          yy: '%dy',
        },
      });

      return moment(currentTime).fromNow();
    },
    renderName() {
      const { user } = this.message;

      if (user.contractor_id) {
        return user.first_name;
      }

      if (user.customer_id) {
        return 'You';
      }

      return `${this.brandName} Support`;
    },
    getIntentButtonInfo() {
      const { link } = this.message;

      if (_.get(link, 'intent') === 'property.details' && _.get(link, 'entity.property_id')) {
        return 'update property details';
      }
      if (_.get(link, 'intent') === 'service.pause') {
        return 'pause subscription';
      }
      if (
        _.get(link, 'intent') === 'service.day-switch' &&
        _.get(link, 'entity.schedule_event_day_switch_id')
      ) {
        return link.entity.rejected_at
          ? 'original service day confirmed'
          : 'keep me on the same day';
      }

      return null;
    },
  },
  methods: {
    ...mapActions([CONVERSATION__REJECT_DAY_SWITCH]),

    onIntentActionPress({ link }) {
      const { message } = this;

      if (link.intent === 'property.details') {
        this.$router.push({
          name: 'propertiesRequiredInfo',
          params: {
            property_id: link.entity.property_id,
          },
        });

        return;
      }

      if (link.intent === 'service.pause') {
        this.$router.push({
          name: 'pauseSubscription',
        });
        return;
      }

      if (link.intent === 'service.day-switch') {
        this[CONVERSATION__REJECT_DAY_SWITCH]({
          message_id: message.message_id,
          schedule_event_day_switch_id: message.link.entity.schedule_event_day_switch_id,
          scheduleEvent: message.link.entity.scheduleEvent,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.border-bottom {
  border-bottom: 2px solid $primary-light;
}

.ls-conversation-message {
  padding: 20px 0px;
  display: flex;
  flex-direction: column;
}

.ls-conversation-message--relevant-property {
  margin-top: 10px;
  font-style: italic;
  color: $tertiary;
  font-weight: bold;
  display: flex;
  align-items: baseline;

  > div {
    height: 17px;
    width: 17px;
    border-radius: 50%;
    color: $white;
    background-color: $tertiary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: $size-5;
    margin-right: 3px;
    font-style: normal;
  }

  @media (max-width: $mobile-max-width) {
    text-align: center;
  }
}

.ls-conversation-message--upper {
  display: flex;
}

.ls-conversation-message--timestamp {
  color: $primary;
}

.ls-conversation-message--primary {
  color: $primary;
}

.ls-conversation-message--gray {
  color: $gray-primary;
}

.ls-conversation-message--tertiary {
  color: $tertiary;
}

.ls-conversation-message--message-text {
  white-space: pre-line;
}

@media (max-width: $mobile-max-width) {
  .ls-conversation-message {
    width: 100%;
    padding: 20px 0px 20px 20px;
  }

  .ls-conversation-message--upper {
    width: 100%;
  }

  .ls-conversation-message--mobile-header {
    display: flex;
    justify-content: space-between;
  }

  .ls-conversation-message--message-text {
    padding: 6px 0;
  }

  .ls-conversation-message--upper {
    display: block;
  }
}
.themed-app {
  .border-bottom {
    border-color: $theme-color-primary-light;
  }
  .ls-conversation-message--relevant-property {
    color: $theme-color-secondary;
    > div {
      color: $theme-color-white;
      background-color: $theme-color-secondary;
      font-size: $theme-txt-body;
    }
  }
  .ls-conversation-message--timestamp {
    color: $theme-color-primary;
  }
  .ls-conversation-message--primary {
    color: $theme-color-primary;
  }
  .ls-conversation-message--gray {
    color: $theme-color-gray-darker;
  }
  .ls-conversation-message--tertiary {
    color: $theme-color-secondary;
  }
}
</style>
